import { useCallback, useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  getSubscription,
  getCredits,
  getBillingInfo,
  getCreditsUsage,
  listCreditTransactions,
  createCustomerPortal,
  getPlans,
} from "@/server/billing";
import type { CreditTransactionResponse, CreditTransactionsResponse } from "@/lib/api/generated";
import { openIntercomChat } from "@/lib/billing-intercom";
import { toSubscriptionResponseStatus } from "@/lib/billing/subscription-status";
import { getPlanPriceValue } from "@/lib/billing/plan-pricing";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import { logError } from "@/lib/structured-log";
import { format, differenceInDays } from "date-fns";
import { PlanEmptyState } from "@/components/billing/PlanEmptyState";
import { PurchaseCreditsDialog } from "@/components/billing/PurchaseCreditsDialog";
import { BillingInfoDialog } from "@/components/billing/BillingInfoDialog";
import { UpgradePlanModal } from "@/components/billing/UpgradePlanModal";
import { CustomPagination } from "@/components/ui/custom-pagination";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { useTranslation } from "react-i18next";
import creditDealIcon from "@/assets/billing/credit-deal.svg";
import creditAddIcon from "@/assets/billing/credit-add.svg";
import iconBillingInfo from "@/assets/billing/icon-billing-info.svg";
import iconEdit from "@/assets/billing/icon-edit.svg";
import iconPayment from "@/assets/billing/icon-payment.svg";
import iconStatus from "@/assets/billing/icon-status.svg";
import iconCreated from "@/assets/billing/icon-created.svg";
import iconInvoice from "@/assets/billing/icon-invoice.svg";
import iconActions from "@/assets/billing/icon-actions.svg";
import upgradeIcon from "@/assets/billing/upgrade.svg";

const CREDIT_TRANSACTION_PAGE_SIZE = 5;

export const Route = createFileRoute("/workspace/$workspaceSlug/settings/workspace/billing/")({
  loader: async ({ context, parentMatchPromise }) => {
    const workspaceId = await getWorkspaceIdWithFallback(
      context,
      "billing-overview",
      parentMatchPromise,
    );
    const [subscription, credits, billingInfo, creditsUsage, plans] = await Promise.all([
      getSubscription({ data: workspaceId }).catch(() => null),
      getCredits({ data: workspaceId }).catch(() => null),
      getBillingInfo({ data: workspaceId }).catch((error) => {
        logError("billing_overview.loader.failed_to_load_billing_info", { error });
        return null;
      }),
      getCreditsUsage({ data: workspaceId }).catch(() => null),
      getPlans().catch(() => []),
    ]);

    return {
      subscription,
      credits,
      billingInfo,
      workspaceId,
      creditsUsage,
      plans,
    };
  },
  component: BillingOverviewPage,
});

function getTransactionDisplayKey(transaction: CreditTransactionResponse): string {
  if (transaction.transaction_type === "consume") {
    return "consume";
  }
  if (transaction.transaction_type === "addon_grant") {
    return "addon_grant";
  }
  if (transaction.transaction_type === "referral_credit") {
    return "referral_credit";
  }
  return "default";
}

function getReferenceDisplayKey(referenceType?: string | null): string {
  if (!referenceType) {
    return "none";
  }
  if (referenceType === "deal") {
    return "deal";
  }
  if (referenceType === "outreach_email") {
    return "outreach_email";
  }
  if (referenceType === "outreach_whatsapp") {
    return "outreach_whatsapp";
  }
  if (referenceType === "outreach_linkedin") {
    return "outreach_linkedin";
  }
  if (referenceType === "referral_reward") {
    return "referral_reward";
  }
  return "default";
}

function BillingOverviewPage() {
  const { t } = useTranslation("billing");
  const { workspaceSlug } = Route.useParams();
  const { subscription, credits, billingInfo, workspaceId, creditsUsage, plans } =
    Route.useLoaderData();
  const availablePlans = plans ?? [];
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionData, setTransactionData] = useState<CreditTransactionsResponse | null>(null);
  const [isTransactionsLoading, setIsTransactionsLoading] = useState(true);
  const transactions = transactionData?.items ?? [];
  const transactionTotal = transactionData?.total ?? transactions.length;
  const transactionTotalPages = Math.max(
    1,
    Math.ceil(transactionTotal / CREDIT_TRANSACTION_PAGE_SIZE),
  );

  const [showPurchaseCredits, setShowPurchaseCredits] = useState(false);
  const [showBillingInfo, setShowBillingInfo] = useState(false);
  const [showUpgradePlan, setShowUpgradePlan] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);

  const customPlanLabel = t("upgrade_modal.custom", "Custom");
  const getPlanPrice = (planType?: string, cycle?: string) => {
    const price = getPlanPriceValue(planType, cycle);
    if (price === null) return customPlanLabel;
    if (!price) return "$0";
    return cycle === "yearly" ? `$${price}/monthly` : `$${price}/monthly`;
  };

  const getPlanPriceWithoutCycle = (planType?: string, cycle?: string) => {
    const price = getPlanPriceValue(planType, cycle);
    if (price === null) return customPlanLabel;
    if (!price) return "$0";
    return `$${price}`;
  };

  const getPlanCredits = (planType?: string) => {
    if (!planType) return 0;
    const matchedPlan = availablePlans.find(
      (plan) => plan.key?.toLowerCase() === planType.toLowerCase(),
    );
    if (matchedPlan?.credits?.subscription_credits != null) {
      return matchedPlan.credits.subscription_credits;
    }
    if (credits?.subscription_credits != null) return credits.subscription_credits;
    return 0;
  };

  const planName = subscription?.plan_type
    ? t(
        `plans.${subscription.plan_type.toLowerCase()}`,
        subscription.plan_type.charAt(0).toUpperCase() + subscription.plan_type.slice(1),
      )
    : t("plans.free", "Free");
  const status = subscription?.status || "active";
  const isTrial = status === "trialing";
  const isPaymentPending = status === "payment_pending";

  const isPlanEmpty =
    !subscription ||
    !subscription.plan_type ||
    subscription.plan_type === "free" ||
    subscription.status === "canceled" ||
    subscription.status === "expired";

  const handleUpgradeClick = () => setShowUpgradePlan(true);
  const handleAddCreditClick = () => setShowPurchaseCredits(true);
  const handleEditBillingClick = () => setShowBillingInfo(true);
  const handleEditPaymentClick = async () => {
    setIsPortalLoading(true);
    try {
      const baseUrl = window.location.origin;
      const returnUrl = `${baseUrl}/workspace/${workspaceSlug}/settings/workspace/billing`;
      const result = await createCustomerPortal({
        data: {
          workspaceId,
          data: {
            return_url: returnUrl,
          },
        },
      });
      if (result?.portal_url) {
        window.location.href = result.portal_url;
      } else {
        throw new Error(t("errors.portal_url_missing", "No portal URL returned"));
      }
    } catch {
      toast.error(t("errors.portal_failed", "Failed to open billing portal"));
      setIsPortalLoading(false);
    }
  };

  const handleCancelClick = () => setShowCancelConfirm(true);

  const confirmCancel = () => {
    setShowCancelConfirm(false);
    openIntercomChat(t("intercom.cancel_subscription", "Cancel Subscription"), {
      workspaceId,
      currentPlan: subscription?.plan_type,
    });
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const fetchTransactionPage = useCallback(
    async (page: number) => {
      setIsTransactionsLoading(true);
      try {
        const nextTransactions = await listCreditTransactions({
          data: {
            workspaceId,
            params: {
              limit: CREDIT_TRANSACTION_PAGE_SIZE,
              offset: (page - 1) * CREDIT_TRANSACTION_PAGE_SIZE,
            },
          },
        });
        setTransactionData(nextTransactions);
        setTransactionPage(page);
      } catch (error) {
        logError("billing_overview.credit_transactions.pagination_failed", { error });
        toast.error(t("errors.credit_transactions_failed", "Failed to load credit records"));
      } finally {
        setIsTransactionsLoading(false);
      }
    },
    [t, workspaceId],
  );

  useEffect(() => {
    void fetchTransactionPage(1);
  }, [fetchTransactionPage]);

  const handleTransactionPageChange = async (page: number) => {
    if (page < 1 || page > transactionTotalPages || page === transactionPage) {
      return;
    }

    await fetchTransactionPage(page);
  };

  const nextRenewalDate = subscription?.current_period_end
    ? new Date(subscription.current_period_end)
    : null;
  const trialEndDate = subscription?.trial_end ? new Date(subscription.trial_end) : null;
  const remainingTrialDays = trialEndDate ? differenceInDays(trialEndDate, new Date()) : 0;

  const renewalDisplay = nextRenewalDate
    ? format(nextRenewalDate, "MMM d, yyyy")
    : t("not_available", "N/A");

  const planPrice = getPlanPrice(subscription?.plan_type, subscription?.billing_cycle);
  const renewalPrice = getPlanPriceWithoutCycle(
    subscription?.plan_type,
    subscription?.billing_cycle,
  );
  const planCredits = getPlanCredits(subscription?.plan_type);
  const billingCycleKey = subscription?.billing_cycle || "monthly";
  const billingCycleLabel = t(
    `billing_cycles.${billingCycleKey}`,
    billingCycleKey === "monthly"
      ? "Monthly"
      : billingCycleKey === "yearly"
        ? "Yearly"
        : billingCycleKey,
  );

  return (
    <div className="space-y-[24px] p-6 max-w-[1920px] font-['PingFang_SC']">
      <div>
        <h2 className="mb-[16px] text-[16px] font-semibold text-gray-900">
          {t("page_title", "Plan & Billing")}
        </h2>

        {isPlanEmpty ? (
          <PlanEmptyState workspaceId={workspaceId} onUpgrade={handleUpgradeClick} />
        ) : (
          /* Subscription Header Box */
          <div className="border border-[#DFDFDF] rounded-2xl bg-white p-6 flex flex-wrap items-center justify-between gap-6 relative overflow-hidden">
            <div className="flex items-center gap-4 min-w-[200px]">
              <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center shrink-0">
                <img
                  src={iconPayment}
                  className="w-5 h-5 invert"
                  alt={t("actions.upgrade", "Upgrade")}
                />
              </div>
              <div>
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-base">
                      {`${planName} ${billingCycleLabel}`}
                    </span>
                    <span
                      className={
                        isPaymentPending
                          ? "text-xs bg-[#FFF7E6] text-[#D46B08] px-2 py-0.5 rounded-full border border-[#FFD591]"
                          : "text-xs bg-[#E6F4FF] text-[#0958D9] px-2 py-0.5 rounded-full border border-[#91CAFF]"
                      }
                    >
                      {isTrial
                        ? t("status.trial", "Trial")
                        : isPaymentPending
                          ? t("status.payment_pending", "Payment pending")
                          : t("status.current_plan", "Current plan")}
                    </span>
                    {isTrial && (
                      <span className="text-xs text-[#FAAD14] ml-2 font-medium">
                        {t("trial_ends_in", "Trial ends in {{days}} days", {
                          days: remainingTrialDays,
                        })}
                      </span>
                    )}
                  </div>
                  <span className="text-sm text-[#7E7E7E] mt-1">{planPrice}</span>
                  {isPaymentPending && (
                    <span className="text-xs text-[#D46B08] mt-1">
                      {t(
                        "pending_payment.message",
                        "Payment pending. Your plan will activate after payment.",
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col min-w-[150px]">
              <span className="font-bold text-sm text-black">
                {t("monthly_credits", "Monthly credits")}
              </span>
              <span className="text-sm text-[#7E7E7E] mt-1">
                {t("active_state.credits_summary", "{{count}} credit/monthly", {
                  count: planCredits,
                })}
              </span>
            </div>

            <div className="flex flex-col min-w-[150px]">
              <span className="font-bold text-sm text-black">
                {t("next_renewal", "Next renewal")}
              </span>
              <span className="text-sm text-[#7E7E7E] mt-1">
                {nextRenewalDate
                  ? t("active_state.renewal_summary", "{{price}} on {{date}}", {
                      price: renewalPrice,
                      date: renewalDisplay,
                    })
                  : renewalDisplay}
              </span>
            </div>

            <div className="flex flex-col items-end gap-2 min-w-[120px]">
              {isPaymentPending ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleEditPaymentClick}
                  disabled={isPortalLoading}
                  className="gap-2 rounded-lg border-black bg-transparent px-4 py-2 text-sm font-medium text-black hover:bg-gray-50 disabled:cursor-not-allowed"
                >
                  {isPortalLoading ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t("actions.redirecting", "Redirecting...")}
                    </span>
                  ) : (
                    <span>{t("actions.pay_now", "Pay now")}</span>
                  )}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleUpgradeClick}
                    className="gap-2 rounded-lg border-black bg-transparent px-4 py-2 text-sm font-medium text-black hover:bg-gray-50"
                  >
                    <img
                      src={upgradeIcon}
                      className="w-4 h-4"
                      alt={t("actions.upgrade", "Upgrade")}
                    />
                    {t("actions.upgrade", "Upgrade")}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleCancelClick}
                    className="h-auto p-0 text-xs text-[#FF4D4F] hover:bg-transparent hover:text-[#FF4D4F] hover:underline"
                  >
                    {t("actions.cancel_subscription", "Cancel subscription")}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-bold mb-[9px] text-black">
          {t("credits_usage", "Credits usage")}
        </h3>
        <div className="border border-[#DFDFDF] rounded-xl bg-white">
          <div className="flex items-center justify-between px-[16px] py-[13px] min-h-[68px]">
            <div className="flex flex-col items-start">
              <div className="flex items-center justify-between w-full gap-[49px]">
                <span className="text-sm font-bold text-black">{t("credits.title", "Credit")}</span>
              </div>
              <div className="flex items-center gap-1 mt-1">
                <img
                  src={creditDealIcon}
                  className="w-[12px] h-[12px]"
                  alt={t("purchase_credits.credits", "Credits")}
                />
                <span className="text-xs text-[#7E7E7E]">
                  {creditsUsage
                    ? `${creditsUsage.used_credits} / ${creditsUsage.total_credits}`
                    : credits?.total_credits !== undefined && credits?.total_credits !== null
                      ? credits.total_credits
                      : 0}
                </span>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleAddCreditClick}
              className="justify-between rounded-lg border-[#DFDFDF] bg-white px-[12px] py-[6px] hover:bg-gray-50"
            >
              <img
                src={creditAddIcon}
                className="w-[12px] h-[12px] mr-2"
                alt={t("actions.add", "Add")}
              />
              <span className="text-xs font-medium">
                {t("actions.add_extra_credit", "Add Extra Credit")}
              </span>
            </Button>
          </div>

          <div className="border-t border-[#EEEFF1] px-[16px] py-[14px]">
            <div className="mb-[10px] flex items-center justify-between gap-4">
              <span className="text-xs font-semibold text-black">
                {t("credits.transactions.title", "Usage records")}
              </span>
              <span className="inline-flex items-center gap-2 text-xs text-[#7E7E7E]">
                {isTransactionsLoading ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {t("credits.transactions.loading", "Loading")}
                  </>
                ) : (
                  t("credits.transactions.page_summary", "Page {{page}} of {{totalPages}}", {
                    page: transactionPage,
                    totalPages: transactionTotalPages,
                  })
                )}
              </span>
            </div>

            <div className="relative" aria-busy={isTransactionsLoading}>
              <div
                className={
                  isTransactionsLoading
                    ? "pointer-events-none opacity-45 transition-opacity"
                    : "transition-opacity"
                }
              >
                {transactions.length > 0 ? (
                  <Table className="min-w-[520px] text-xs">
                    <TableHeader>
                      <TableRow className="border-[#EEEFF1] hover:bg-transparent">
                        <TableHead className="h-8 px-0 pr-4 text-[11px] font-medium text-[#7E7E7E]">
                          {t("credits.transactions.columns.contact", "Contact")}
                        </TableHead>
                        <TableHead className="h-8 px-0 pr-4 text-[11px] font-medium text-[#7E7E7E]">
                          {t("credits.transactions.columns.credits_used", "Credits used")}
                        </TableHead>
                        <TableHead className="h-8 px-0 pr-4 text-[11px] font-medium text-[#7E7E7E]">
                          {t("credits.transactions.columns.feature", "Feature")}
                        </TableHead>
                        <TableHead className="h-8 px-0 text-right text-[11px] font-medium text-[#7E7E7E]">
                          {t("credits.transactions.columns.billing_date", "Billing date")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((transaction) => {
                        const displayKey = getTransactionDisplayKey(transaction);
                        const referenceKey = getReferenceDisplayKey(transaction.reference_type);
                        const isCreditIncrease = transaction.credits_delta > 0;
                        const amountLabel = `${isCreditIncrease ? "+" : ""}${transaction.credits_delta}`;
                        const featureLabel =
                          transaction.reference_type != null
                            ? t(
                                `credits.transactions.references.${referenceKey}`,
                                transaction.reference_type,
                              )
                            : t(
                                `credits.transactions.types.${displayKey}`,
                                transaction.transaction_type,
                              );

                        return (
                          <TableRow key={transaction.id} className="border-[#EEEFF1]">
                            <TableCell className="max-w-[240px] px-0 py-[10px] pr-4 text-black">
                              <span className="block truncate">
                                {transaction.contact_name ?? t("not_available", "N/A")}
                              </span>
                            </TableCell>
                            <TableCell
                              className={
                                isCreditIncrease
                                  ? "px-0 py-[10px] pr-4 font-semibold text-[#389E0D]"
                                  : "px-0 py-[10px] pr-4 font-semibold text-[#D4380D]"
                              }
                            >
                              {amountLabel}
                            </TableCell>
                            <TableCell className="max-w-[320px] px-0 py-[10px] pr-4 text-black">
                              <span className="block truncate">{featureLabel}</span>
                            </TableCell>
                            <TableCell className="px-0 py-[10px] text-right text-[#7E7E7E]">
                              {format(new Date(transaction.created_at), "MMM d, yyyy HH:mm")}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="py-[18px] text-center text-xs text-[#7E7E7E]">
                    {t("credits.transactions.empty", "No credit records yet")}
                  </div>
                )}
              </div>
              {isTransactionsLoading ? (
                <div className="absolute inset-0 flex items-center justify-center bg-white/55">
                  <div className="inline-flex items-center gap-2 rounded-md border border-[#EEEFF1] bg-white px-3 py-2 text-xs font-medium text-[#7E7E7E] shadow-sm">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {t("credits.transactions.loading", "Loading")}
                  </div>
                </div>
              ) : null}
            </div>
            {transactionTotalPages > 1 ? (
              <div className="pt-3">
                <CustomPagination
                  currentPage={transactionPage}
                  totalPages={transactionTotalPages}
                  totalItems={transactionTotal}
                  pageSize={CREDIT_TRANSACTION_PAGE_SIZE}
                  onPageChange={handleTransactionPageChange}
                  onPageSizeChange={() => undefined}
                  loading={isTransactionsLoading}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold mb-[16px] text-black">
          {t("billing_details", "Billing Details")}
        </h3>

        <div className="border border-[#DFDFDF] rounded-2xl p-[16px] flex justify-between items-start mb-[12px] bg-white">
          <div className="flex items-start gap-3">
            <div className="w-[24px] h-[24px] bg-[#F6F7F8] rounded-[6px] flex items-center justify-center border border-[#EEEFF1] shrink-0">
              <img
                src={iconBillingInfo}
                className="w-[12px] h-[12px]"
                alt={t("billing_info.title", "Billing information")}
              />
            </div>
            <div>
              <p className="text-sm font-bold text-black">
                {t("billing_info.title", "Billing information")}
              </p>
              {billingInfo?.company_name ? (
                <div className="text-xs text-[#7E7E7E] mt-1 leading-relaxed">
                  <p>{billingInfo.company_name}</p>
                  <p>
                    {[
                      billingInfo.address_line1,
                      billingInfo.city,
                      billingInfo.state,
                      billingInfo.billing_email,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                  <p>
                    {t("billing_info.tax_id", "VAT/GST ID")}:{" "}
                    {billingInfo.tax_id || t("not_available", "N/A")}
                  </p>
                </div>
              ) : (
                <div className="text-xs text-[#7E7E7E] mt-1 leading-relaxed italic">
                  {t(
                    "billing_info.empty",
                    "Add your business information to show on every invoice",
                  )}
                </div>
              )}
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleEditBillingClick}
            className="rounded-lg border-[#DFDFDF] bg-white px-[10px] py-[4px] hover:bg-gray-50"
          >
            <img
              src={iconEdit}
              className="w-[12px] h-[12px] mr-1"
              alt={t("actions.edit", "Edit")}
            />
            <span className="text-xs font-medium">{t("actions.edit", "Edit")}</span>
          </Button>
        </div>

        <div className="border border-[#DFDFDF] rounded-2xl p-[16px] flex justify-between items-start bg-white">
          <div className="flex items-start gap-3">
            <div className="w-[24px] h-[24px] bg-[#F6F7F8] rounded-[6px] flex items-center justify-center border border-[#EEEFF1] shrink-0">
              <img
                src={iconPayment}
                className="w-[12px] h-[12px]"
                alt={t("payment_method.title", "Payment")}
              />
            </div>
            <div>
              <p className="text-sm font-bold text-black">
                {t("payment_method.title", "Payment method")}
              </p>
              <div className="text-xs text-[#7E7E7E] mt-1 leading-relaxed italic">
                {t("payment_method.empty", "No payment method added")}
              </div>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleEditPaymentClick}
            disabled={isPortalLoading}
            className="rounded-lg border-[#DFDFDF] bg-white px-[10px] py-[4px] hover:bg-gray-50 disabled:cursor-not-allowed"
          >
            {isPortalLoading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-[12px] h-[12px] animate-spin" />
                <span className="text-xs font-medium">
                  {t("actions.redirecting", "Redirecting...")}
                </span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <img
                  src={iconEdit}
                  className="w-[12px] h-[12px]"
                  alt={
                    isPaymentPending
                      ? t("actions.pay_now", "Pay now")
                      : t("actions.manage", "Manage")
                  }
                />
                <span className="text-xs font-medium">
                  {isPaymentPending
                    ? t("actions.pay_now", "Pay now")
                    : t("actions.manage", "Manage")}
                </span>
              </span>
            )}
          </Button>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold mb-[18px] text-black">
          {t("recent_invoices", "Recent invoices")}
        </h3>
        <div className="relative w-full rounded-xl overflow-hidden bg-white border border-[#DFDFDF]">
          <div className="bg-[#F6F7F8] h-[40px] flex items-center text-sm border-b border-[#DFDFDF]">
            <div className="flex items-center pl-[16px] w-[25%]">
              <img src={iconInvoice} className="w-[14px] h-[14px] mr-2" alt="" />
              <span className="font-bold text-black">{t("invoices.invoice", "Invoice")}</span>
            </div>
            <div className="flex items-center pl-[16px] w-[15%]">
              <img src={iconStatus} className="w-[14px] h-[14px] mr-2" alt="" />
              <span className="font-bold text-black">{t("invoices.status", "Status")}</span>
            </div>
            <div className="flex items-center pl-[16px] w-[20%]">
              <img src={iconPayment} className="w-[14px] h-[14px] mr-2" alt="" />
              <span className="font-bold text-black">{t("invoices.amount", "Amount")}</span>
            </div>
            <div className="flex items-center pl-[16px] w-[25%]">
              <img src={iconCreated} className="w-[14px] h-[14px] mr-2" alt="" />
              <span className="font-bold text-black">{t("invoices.created", "Created")}</span>
            </div>
            <div className="flex items-center pl-[16px] w-[15%]">
              <img src={iconActions} className="w-[14px] h-[14px] mr-2" alt="" />
              <span className="font-bold text-black">{t("invoices.actions", "Actions")}</span>
            </div>
          </div>

          <div className="bg-white p-8 text-center text-[#7E7E7E] text-sm">
            {t("invoices.empty", "No invoices found")}
          </div>
        </div>
      </div>

      <PurchaseCreditsDialog
        workspaceId={workspaceId}
        workspaceSlug={workspaceSlug}
        open={showPurchaseCredits}
        onOpenChange={setShowPurchaseCredits}
      />

      <BillingInfoDialog
        workspaceId={workspaceId}
        initialData={billingInfo}
        open={showBillingInfo}
        onOpenChange={setShowBillingInfo}
        onSuccess={handleRefresh}
      />

      <UpgradePlanModal
        workspaceId={workspaceId}
        workspaceSlug={workspaceSlug}
        currentPlan={subscription?.plan_type}
        subscriptionStatus={toSubscriptionResponseStatus(subscription?.status)}
        open={showUpgradePlan}
        onOpenChange={setShowUpgradePlan}
      />

      <Dialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("active_state.cancel_confirm")}</DialogTitle>
            <DialogDescription>{t("active_state.cancel_desc")}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelConfirm(false)}>
              {t("actions.back")}
            </Button>
            <Button
              onClick={confirmCancel}
              className="bg-[#FF4D4F] hover:bg-[#FF4D4F]/90 text-white"
            >
              {t("actions.confirm_cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
